import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { ACCOUNT_LOGIN, ACCOUNT_REGISTER } from "@/lib/accountUrl";

export default function Navbar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const html = document.documentElement;
    const prevBody = document.body.style.overflow;
    const prevHtml = html.style.overflow;
    document.body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    window.__lenis?.stop?.();
    return () => {
      document.body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
      window.__lenis?.start?.();
    };
  }, [open]);

  const NAV = [
    { label: t("nav.home"), to: "/" },
    { label: t("nav.markets"), to: "/markets" },
    { label: t("nav.bots"), to: "/copy-trading" },
    { label: t("nav.predict"), to: "/prediction-market" },
    { label: t("nav.earn"), to: "/earn" },
    { label: t("nav.about"), to: "/about" },
    { label: t("nav.help"), to: "/help" },
    { label: t("nav.contact"), to: "/contact" },
  ];

  const overHero = pathname === "/" && !scrolled;
  const closeMobile = () => setOpen(false);

  const mobileMenu = createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.button
            type="button"
            aria-label="Close menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-[80] bg-ink-900/40"
            onClick={closeMobile}
          />
          <motion.aside
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "tween", duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden fixed inset-y-0 left-0 z-[90] h-dvh w-[min(86vw,300px)] bg-white border-r border-ink-100 shadow-pop flex flex-col overflow-hidden"
            data-testid="front-mobile-menu"
            data-lenis-prevent
          >
            <div className="h-14 flex items-center justify-between gap-3 px-3 border-b border-ink-100 shrink-0">
              <Link to="/" onClick={closeMobile} className="flex items-center gap-2 min-w-0">
                <img src={`${process.env.PUBLIC_URL}/logo-b.png`} alt="FutureChain" className="h-7 w-auto object-contain" />
              </Link>
              <button type="button" onClick={closeMobile} className="p-1.5 rounded-lg text-ink-600 hover:bg-surface-soft" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y p-2.5 space-y-0.5" data-lenis-prevent>
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={closeMobile}
                  data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-ink-700 hover:bg-surface-soft hover:text-brand-600"
                >
                  {n.label}
                </Link>
              ))}

              <div className="hairline my-1.5" />

              <div className="px-1.5 py-0.5">
                <LanguageSwitcher />
              </div>

              <div className="flex flex-col gap-1.5 p-1.5 pt-2">
                <a href={ACCOUNT_LOGIN} onClick={closeMobile} className="px-3 py-2 rounded-lg text-center text-sm font-medium text-ink-800 border border-ink-100">{t("nav.signin")}</a>
                <a href={ACCOUNT_REGISTER} onClick={closeMobile} className="btn-primary px-3 py-2 rounded-lg text-center text-sm font-semibold">{t("nav.signup")}</a>
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,border-color,backdrop-filter,box-shadow] duration-300 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-ink-100"
            : overHero
              ? "bg-transparent border-b border-transparent"
              : "bg-white/60 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="container-x section-x h-16 md:h-[72px] flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 flex-shrink-0">
            <img
              src={`${process.env.PUBLIC_URL}${overHero ? "/logo-w.png" : "/logo-b.png"}`}
              alt="FutureChain"
              className="h-8 md:h-9 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium ${
                  overHero
                    ? `text-white/85 hover:text-white hover:bg-white/10 ${isActive ? "text-white" : ""}`
                    : `text-ink-700 hover:text-brand-600 hover:bg-brand-50/50 ${isActive ? "text-brand-600" : ""}`
                }`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-1">
            <LanguageSwitcher light={overHero} />
            <a href={ACCOUNT_LOGIN} data-testid="nav-login-btn" className={`px-4 py-2 rounded-lg text-sm font-semibold ${overHero ? "text-white/90 hover:bg-white/10" : "text-ink-800 hover:bg-surface-soft"}`}>{t("nav.signin")}</a>
            <a href={ACCOUNT_REGISTER} data-testid="nav-register-btn" className="btn-primary px-4 py-2 rounded-lg text-sm font-semibold">{t("nav.signup")}</a>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className={`lg:hidden p-2 rounded-lg ${overHero ? "text-white hover:bg-white/10" : "text-ink-800 hover:bg-surface-soft"}`}
            aria-label="Menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {mobileMenu}
    </>
  );
}
